import {bootstrapCameraKit, createMediaStreamSource, Transform2D} from "@snap/camera-kit";

class Camera {
    static FRONT_CAMERA = 'user';
    static BACK_CAMERA = 'environment';
    static notSuitableCameras = [
        'wide', 'dual', 'ultra', 'triple', 'telephoto',
        'ширококутна', 'здвоєна', 'надширококутна', 'потроєна', 'телефотокамера'
    ];

    lensIndex = 1;
    lenses = [];
    cameraFacing = Camera.FRONT_CAMERA;
    cameras = [];
    frontCamera = null;
    backCamera = null;
    isReady = false;
    currentStream = null;

    constructor(showDebug) {
        this.showDebug = showDebug;
        this.video = document.createElement('video');
        this.video.setAttribute('muted', '');
        console.log(this.video);
    }

    initialize = async (config) => {
        const {
            apiToken = '',
            lensGroupId = '',
            canvasRef = null,
            cameraFacing = Camera.FRONT_CAMERA,
            lensIndex = 0,
        } = config;

        this.canvasRef = canvasRef;
        this.cameraFacing = cameraFacing;
        this.lensIndex = lensIndex;

        // cameras initialization
        await this.requestCameraPermission();
        await this.prepareCameraList();
        this.printInfo();

        // cameraKit initialization
        // Canvas MUST be sized before createSession, which calls transferControlToOffscreen
        canvasRef.width = Math.round(window.innerWidth * window.devicePixelRatio);
        canvasRef.height = Math.round(window.innerHeight * window.devicePixelRatio);

        const cameraKit = await bootstrapCameraKit({ apiToken });
        this.session = await cameraKit.createSession({
            liveRenderTarget: canvasRef
        });
        const {lenses} = await cameraKit.lensRepository.loadLensGroups([ lensGroupId ]);
        this.lenses = lenses;
        console.log(`Loaded ${lenses.length} lenses`, lenses);

        // cameraKit starting
        await this.startCameraKit();

        // apply lens
        await this.applyLens(lensIndex);

        return this;
    }

    startCameraKit = async () => {
        this.isReady = false;
        const mediaStream = await this.getMediaStream();
        this.currentStream = mediaStream;
        const mediaStreamSourceOptions = {
            transform: this.cameraFacing === Camera.FRONT_CAMERA ? Transform2D.MirrorX : Transform2D.Identity,
            fpsLimit: 60,
            cameraType: this.cameraFacing,
        };
        const streamSource = createMediaStreamSource(mediaStream, mediaStreamSourceOptions);
        const screenWidth = Math.round(window.innerWidth * window.devicePixelRatio);
        const screenHeight = Math.round(window.innerHeight * window.devicePixelRatio);

        /** TODO: wtf? */
        await new Promise(resolve => setTimeout(resolve, 1000));
        /** ^^^^^ */
        await this.session.setSource(streamSource);

        try {
            await streamSource.setRenderSize(screenWidth, screenHeight);
        } catch (e) {
            console.log(e.message);
        }
        this.isReady = true;
    }

    changeCameraFacing = async (facing) => {
        if (!this.isReady) return;

        if (this.currentStream) {
            this.closeStream(this.currentStream);
            this.currentStream = null;
        }

        this.cameraFacing = facing;
        await this.startCameraKit();
        await this.applyLens(this.lensIndex);
    }

    applyLens = async (index) => {
        if (!this.isReady) return;
        this.lensIndex = index;
        const lens = this.lenses[index];
        if (lens) await this.session.applyLens(lens);
        await this.session.play('live');
    }

    pause = async () => {
        if (!this.session) return;
        await this.session.pause();
    }

    resume = async () => {
        if (!this.session) return;
        await this.session.play('live');
    }

    requestCameraPermission = async () => {
        const constraints = { video: true, audio: false };
        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        this.closeStream(stream);
    }

    closeStream(stream) {
        if (stream) {
            const tracks = stream.getTracks();
            for (let i = 0; i < tracks.length; i++) {
                const track = tracks[i];
                track.stop();
            }
        }
    }

    async prepareCameraList() {
        let cams = await navigator.mediaDevices.enumerateDevices();
        cams = cams.filter(cam => cam.kind === 'videoinput');
        for (const cam of cams) {
            try {
                const camera = {
                    label: cam.label,
                    deviceId: cam.deviceId,
                };
                const video = document.createElement('video');
                video.setAttribute("playsinline", "playsinline");
                video.muted = true;
                const constraints = {
                    audio: false,
                    video: {
                        height: { ideal: 3024 },
                        frameRate: { ideal: 60 },
                        deviceId: cam.deviceId,
                    },
                };
                const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
                video.srcObject = mediaStream;
                const [videoTrack] = mediaStream.getVideoTracks();
                const capabilities = videoTrack.getCapabilities();
                camera.facingMode = capabilities.facingMode;
                camera.height = capabilities.height?.max || 0;
                camera.width = capabilities.width?.max || 0;
                this.cameras.push(camera);
                videoTrack.stop();
            } catch (error) {
                console.error(error);
            }
        }

        console.log(`Found ${this.cameras.length} cameras`, this.cameras);
        if (!this.frontCamera && this.cameras.length > 0) {
            this.frontCamera = this.cameras.filter(camera => {
                return camera.facingMode.includes('user');
            })[0] || this.cameras[0];
        }
        if (!this.backCamera && this.cameras.length > 0) {
            this.backCamera = this.cameras.filter(camera => {
                return camera.facingMode.includes('environment')
                    && !Camera.notSuitableCameras.some(word => camera.label.toLowerCase().includes(word));
            })[0] || this.cameras.at(-1);
        }

        console.log({
            frontCamera: this.frontCamera,
            backCamera: this.backCamera,
        });
    }

    async getMediaStream() {
        const videoConstraints = {
            // 540
            height: { ideal: 720 },
            frameRate: { ideal: 60 },
            deviceId: this.cameraFacing === Camera.FRONT_CAMERA
                ? this.frontCamera?.deviceId
                : this.backCamera?.deviceId,
        };

        const mediaConstraints = {
            video: videoConstraints,
            audio: false
        };

        return await navigator.mediaDevices.getUserMedia(mediaConstraints);
    }

    printInfo() {
        if (!this.showDebug) return;
        console.log('front cameras', this.frontCamera);
        console.log('back cameras', this.backCamera);
        this.showDebug(`Front: ${this.frontCamera.label} (${this.frontCamera.facingMode})`);
        this.showDebug(`Back: ${this.backCamera.label} (${this.backCamera.facingMode})`);
    }
}

export default Camera;
